import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Building2, CheckCircle2, ChevronDown, ChevronRight, Copy, Download, Eye, EyeOff, Flame, HardHat, ListChecks, Loader2, MessageSquare, Search, Settings, Truck, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { usePendingDeliveries } from "@/hooks/usePendingDeliveries";
import { DeliveryMenPanel } from "@/components/DeliveryMenPanel";
import { AgencyLoginButton, AgencyLoginsCard } from "@/components/AgencyLogins";
import { DoneTab } from "@/components/DoneTab";
import { ResolveDialog } from "@/components/ResolveDialog";
import { HEAD, Pill, PdLoading, cdFilename, copyText, fmtIst, mask } from "@/components/PdPill";

const COMPANIES = [["INDANE", "INDANE"], ["BHARAT_GAS", "BHARAT"], ["HP_GAS", "HP"], ["OTHER", "OTHER"]];
const DATES = [["today", "Today"], ["yesterday", "Yesterday"], ["last7", "Last 7d"], ["all", "All Time"]];
const card = "bg-white rounded-xl border border-[#E5E5DF] shadow-sm";
const lbl = "text-[10px] font-semibold uppercase tracking-[0.18em] text-[#6B6B61]";
const GRID = "md:grid-cols-[minmax(190px,1.3fr)_minmax(130px,1fr)_minmax(100px,0.7fr)_minmax(150px,1fr)_minmax(270px,auto)]";

export default function PendingDeliveries() {
  const { user } = useAuth();
  if (user?.role === "delivery_man") return <Navigate to="/delivery" replace />;
  return <AdminPending />;
}

function AdminPending() {
  const navigate = useNavigate();
  const [date, setDate] = useState("today");
  const [company, setCompany] = useState("all");
  const [tab, setTab] = useState("pending");
  const [q, setQ] = useState("");
  const [agencyF, setAgencyF] = useState("all");
  const [dmF, setDmF] = useState("all");
  const [showDm, setShowDm] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [hide, setHide] = useState(() => localStorage.getItem("delivery.hideLpgIds") === "1");
  const [resolving, setResolving] = useState(null);
  const firstLoad = useRef(true);
  const { data, load, mark, undo, busy } = usePendingDeliveries({ date, company });

  useEffect(() => localStorage.setItem("delivery.hideLpgIds", hide ? "1" : "0"), [hide]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("pd-settings-changed", h);
    return () => window.removeEventListener("pd-settings-changed", h);
  }, [load]);
  useEffect(() => {
    if (!data) return;
    if (company !== "all" && data.enabled_companies && !data.enabled_companies.includes(company)) setCompany("all");
    if (agencyF !== "all" && !data.agencies.some((a) => a.group_id === agencyF)) setAgencyF("all");
    if (firstLoad.current && data.agencies.length) {
      firstLoad.current = false;
      setExpanded(new Set([data.agencies[0].group_id]));
    }
  }, [data, company, agencyF]);

  const agencies = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    const dm = data.delivery_men?.find((d) => d.id === dmF);
    return data.agencies
      .filter((a) => agencyF === "all" || a.group_id === agencyF)
      .filter((a) => dmF === "all" || (dmF === "unassigned"
        ? a.company === "INDANE" && !(a.delivery_men || []).length
        : (dm?.delivery_agency_ids || []).includes(a.group_id)))
      .map((a) => ({ ...a, rows: (a[tab] || []).filter((r) => !needle || a.name.toLowerCase().includes(needle) || r.lpg_id.includes(needle) || (r.mobile || "").includes(needle) || (r.dac_code || "").includes(needle)) }))
      .filter((a) => a.rows.length > 0);
  }, [data, tab, q, agencyF, dmF]);
  const logins = useMemo(() => {
    if (!data?.logins) return [];
    const assigned = new Set((data.delivery_men || []).flatMap((d) => d.delivery_agency_ids || []));
    const dm = data.delivery_men?.find((d) => d.id === dmF);
    return data.logins
      .filter((l) => agencyF === "all" || l.group_id === agencyF)
      .filter((l) => dmF === "all" || (dmF === "unassigned" ? !assigned.has(l.group_id) : (dm?.delivery_agency_ids || []).includes(l.group_id)));
  }, [data, agencyF, dmF]);

  const onMark = async (r, status) => {
    if (await mark(r, status, mask(r.lpg_id, hide))) {
      toast.success(status === "correct" ? "Delivered ✓" : status === "wrong" ? "DAC ✗ marked" : "Cancelled marked");
    }
  };
  const exportXlsx = async () => {
    try {
      const res = await api.get("/pending-deliveries/export.xlsx", { params: { date, company, agency: agencyF }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = cdFilename(res, "PENDING.xlsx");
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const toggleAgency = (id) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const t = data?.totals;

  return (
    <div className="min-h-screen bg-[#F4F3EC]" data-testid="pending-deliveries-page">
      <header className="bg-[#385C4B] text-white sticky top-0 z-30 shadow-[0_6px_20px_rgba(56,92,75,0.2)]">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 h-14 flex items-center gap-2.5">
          <button onClick={() => navigate("/home")} className="grid place-items-center w-9 h-9 rounded-full hover:bg-white/10 transition-colors" data-testid="pd-back" aria-label="Back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Truck className="w-5 h-5" />
          <h1 className={`${HEAD} text-lg sm:text-xl font-semibold`}>Pending Deliveries</h1>
          <span className="text-[10px] font-mono uppercase bg-white/15 px-2 py-0.5 rounded-full">Admin</span>
          <span className="ml-auto flex items-center gap-1.5 text-[10px] font-mono tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />LIVE
          </span>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-3 sm:px-6 py-5 space-y-4">
        <StatCards t={t} />
        <Toolbar {...{ tab, setTab, t, hide, setHide, exportXlsx, openDm: () => setShowDm(true) }} />
        <FilterBar {...{ tab, date, setDate, company, setCompany, data, agencyF, setAgencyF, dmF, setDmF, q, setQ }} />
        {showDm && <DeliveryMenPanel onClose={() => setShowDm(false)} onChanged={load} />}
        {tab === "done" ? (
          <DoneTab hide={hide} isAdmin company={company} onChanged={load} />
        ) : (
          <>
            {data && <AgencyLoginsCard logins={logins} isAdmin />}
            {data?.recent?.length > 0 && <RecentStrip rows={data.recent} hide={hide} undo={undo} minutes={data.undo_minutes} openDone={() => setTab("done")} />}
            {!data ? <PdLoading /> : agencies.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[#DCDCD3] bg-white/60 py-12 text-center text-sm text-[#6B6B61]" data-testid="pd-empty">Koi {tab} delivery nahi mili.</div>
            ) : (
              agencies.map((a) => (
                <AgencyBlock key={a.group_id} a={a} tab={tab} open={expanded.has(a.group_id)} onToggle={() => toggleAgency(a.group_id)}
                  hide={hide} busy={busy} onMark={onMark} onResolve={setResolving} navigate={navigate} />
              ))
            )}
          </>
        )}
      </main>
      {resolving && <ResolveDialog row={resolving} hide={hide} onClose={() => setResolving(null)} onDone={load} />}
    </div>
  );
}

const STATS = [
  ["pending", "Pending", ListChecks, "text-[#385C4B]"],
  ["complaint", "Complaint", AlertTriangle, "text-[#D9534F]"],
  ["urgent", "Urgent >24h", Flame, "text-[#E89B35]"],
  ["done_today", "Aaj Done", CheckCircle2, "text-[#385C4B]"],
  ["agencies", "Agencies", Building2, "text-[#8A6046]"],
];

function StatCards({ t }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="pd-totals">
      {STATS.map(([k, l, Icon, tone], i) => (
        <div key={k} className={`${card} p-4 animate-in fade-in slide-in-from-bottom-1 fill-mode-both`} style={{ animationDelay: `${i * 50}ms` }} data-testid={`pd-stat-${k}`}>
          <div className="flex items-center justify-between">
            <span className={lbl}>{l}</span>
            <Icon className={`w-4 h-4 ${tone}`} />
          </div>
          <div className={`${HEAD} text-3xl font-semibold tabular-nums mt-2 text-[#1A1A1A]`} data-testid={k === "done_today" ? "pd-my-done-today" : `pd-stat-${k}-value`}>
            {t ? t[k] : <Loader2 className="w-5 h-5 animate-spin text-[#C0C0B8]" />}
          </div>
        </div>
      ))}
    </div>
  );
}

function Toolbar({ tab, setTab, t, hide, setHide, exportXlsx, openDm }) {
  const tabs = [["pending", "Pending", t?.pending], ["complaint", "Complaint", t?.complaint], ["done", "Done", t?.done_today]];
  const btn = "inline-flex items-center gap-1.5 h-9 px-3.5 text-sm font-semibold rounded-lg border transition-colors";
  return (
    <div className={`${card} p-2 flex flex-wrap items-center gap-2`} data-testid="pd-toolbar">
      <div className="inline-flex p-1 rounded-lg bg-[#F4F3EC]" role="tablist">
        {tabs.map(([k, l, n]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`pd-tab-${k}`} role="tab" aria-selected={tab === k}
            className={`h-9 px-3 sm:px-4 rounded-md text-sm font-semibold inline-flex items-center gap-2 transition-colors ${tab === k ? "bg-[#385C4B] text-white shadow-sm" : "text-[#3A3A35] hover:bg-white"}`}>
            {l}<span className={`text-[11px] font-mono px-1.5 rounded ${tab === k ? "bg-white/20" : "bg-white text-[#6B6B61]"}`}>{n ?? "–"}</span>
          </button>
        ))}
      </div>
      <div className="ml-auto flex flex-wrap gap-2">
        <button onClick={() => setHide((v) => !v)} data-testid="pd-toggle-ids"
          className={`${btn} ${hide ? "bg-[#385C4B] text-white border-[#385C4B]" : "bg-white text-[#385C4B] border-[#E0DFD6] hover:bg-[#F4F3EC]"}`}>
          {hide ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}{hide ? "Show IDs" : "Hide IDs"}
        </button>
        <button onClick={openDm} data-testid="pd-delivery-men" className={`${btn} bg-white text-[#385C4B] border-[#385C4B]/40 hover:bg-[#F4F3EC]`}>
          <HardHat className="w-4 h-4" />Delivery Men
        </button>
        {tab !== "done" && (
          <button onClick={exportXlsx} data-testid="pd-export" className={`${btn} bg-[#385C4B] text-white border-[#385C4B] hover:bg-[#2D4A3C]`}>
            <Download className="w-4 h-4" />Excel
          </button>
        )}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className={`${lbl} w-24 shrink-0`}>{label}</span>
      {children}
    </div>
  );
}

function FilterBar({ tab, date, setDate, company, setCompany, data, agencyF, setAgencyF, dmF, setDmF, q, setQ }) {
  const sel = "h-9 px-3 text-sm border border-[#E0DFD6] rounded-lg bg-white max-w-[280px] focus:outline-none focus:ring-2 focus:ring-[#385C4B]/30";
  return (
    <div className={`${card} p-4 space-y-3`} data-testid="pd-filter-bar">
      {tab !== "done" && (
        <Row label="Date">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {DATES.map(([k, l]) => <Pill key={k} tone="brown" on={date === k} onClick={() => setDate(k)} testid={`pd-date-${k}`}>{l}</Pill>)}
          </div>
        </Row>
      )}
      <CompanyRow {...{ company, setCompany, data }} />
      {tab !== "done" && data && (
        <Row label="Agency">
          <select value={agencyF} onChange={(e) => setAgencyF(e.target.value)} className={sel} data-testid="pd-agency-filter">
            <option value="all">All agencies</option>
            {[...data.agencies].sort((a, b) => a.name.localeCompare(b.name)).map((a) => <option key={a.group_id} value={a.group_id}>{a.name}</option>)}
          </select>
          <span className={`${lbl} sm:ml-4`}>Delivery Man</span>
          <select value={dmF} onChange={(e) => setDmF(e.target.value)} className={sel} data-testid="pd-dm-filter">
            <option value="all">All</option>
            {(data.delivery_men || []).map((d) => <option key={d.id} value={d.id}>{`${d.display_name} (@${d.username}) · aaj ${d.today_done}`}</option>)}
            <option value="unassigned">Unassigned (INDANE)</option>
          </select>
        </Row>
      )}
      {tab !== "done" && (
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A80]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search agency / LPG ID / mobile / DAC…" data-testid="pd-search"
            className="w-full h-10 pl-9 pr-3 text-sm border border-[#E0DFD6] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#385C4B]/30" />
        </div>
      )}
    </div>
  );
}

function CompanyRow({ company, setCompany, data }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const enabled = data?.enabled_companies || COMPANIES.map(([k]) => k);
  const toggle = async (k) => {
    const next = enabled.includes(k) ? enabled.filter((x) => x !== k) : [...enabled, k];
    if (!next.length) return toast.error("Kam se kam ek company ON rakho");
    setSaving(true);
    try {
      await api.put("/pending-deliveries/settings", { enabled_companies: next });
      toast.success(`✓ Saved · ${COMPANIES.find(([c]) => c === k)[1]} ${next.includes(k) ? "ON" : "OFF"}`);
      if (!next.includes(k) && company === k) setCompany("all");
      window.dispatchEvent(new Event("pd-settings-changed"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-2">
      <Row label="Company">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide items-center">
          <Pill on={company === "all"} onClick={() => setCompany("all")} testid="pd-company-all">All</Pill>
          {COMPANIES.filter(([k]) => enabled.includes(k)).map(([k, l]) => <Pill key={k} on={company === k} onClick={() => setCompany(k)} testid={`pd-company-${k}`}>{l}</Pill>)}
          <button onClick={() => setOpen((v) => !v)} title="Companies ON / OFF" data-testid="pd-company-settings"
            className={`grid place-items-center w-8 h-8 rounded-full border transition-colors ${open ? "bg-[#385C4B] text-white border-[#385C4B]" : "bg-white border-[#E0DFD6] text-[#385C4B] hover:bg-[#F4F3EC]"}`}>
            <Settings className="w-3.5 h-3.5" />
          </button>
        </div>
      </Row>
      {open && (
        <div className="flex flex-wrap gap-2 items-center bg-[#F4F3EC] border border-[#E0DFD6] rounded-lg px-3 py-2.5 sm:ml-[104px]" data-testid="pd-company-switches">
          <span className={lbl}>Companies on page:</span>
          {COMPANIES.map(([k, l]) => {
            const on = enabled.includes(k);
            return (
              <button key={k} onClick={() => toggle(k)} disabled={saving} role="switch" aria-checked={on} data-testid={`pd-company-switch-${k}`}
                className={`flex items-center gap-1.5 h-8 px-3 rounded-full text-[11px] font-bold border transition-colors ${on ? "bg-[#385C4B] text-white border-[#385C4B]" : "bg-white text-[#6B6B61] border-[#DCDCD3]"}`}>
                <span className={`w-2 h-2 rounded-full ${on ? "bg-emerald-300" : "bg-[#DCDCD3]"}`} />{l} {on ? "ON" : "OFF"}
              </button>
            );
          })}
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        </div>
      )}
    </div>
  );
}

function RecentStrip({ rows, hide, undo, minutes, openDone }) {
  const r = rows[0];
  return (
    <div className="bg-[#FFF8E6] border border-[#F0DFA5] rounded-xl px-3 py-2 flex items-center gap-2 text-sm" data-testid="pd-recent" title={`${minutes} min tak Undo`}>
      <span className="font-bold text-[#7A5A1F] whitespace-nowrap" data-testid="pd-recent-count">{rows.length} abhi mark kiye</span>
      <span className="text-[#7A5A1F] truncate min-w-0">· Last: <span className="font-mono">{mask(r.lpg_id, hide)}</span></span>
      <button onClick={() => undo(r)} data-testid="pd-recent-undo"
        className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-[#7A5A1F]/60 text-[#7A5A1F] bg-white text-xs font-semibold shrink-0 hover:bg-[#FFF1CC] transition-colors">
        <Undo2 className="w-3.5 h-3.5" />Undo
      </button>
      <button onClick={openDone} data-testid="pd-recent-see-all" className="ml-auto font-bold text-[#385C4B] whitespace-nowrap shrink-0 hover:underline">Sab dekho ›</button>
    </div>
  );
}

const smallBtn = "inline-flex items-center gap-1 h-8 px-2.5 text-[11px] font-semibold uppercase tracking-wide rounded-lg border border-[#E0DFD6] bg-white hover:bg-[#F4F3EC] transition-colors";

function AgencyBlock({ a, tab, open, onToggle, hide, busy, onMark, onResolve, navigate }) {
  const dms = a.delivery_men || [];
  return (
    <section className={`${card} overflow-hidden`} data-testid={`pd-agency-${a.group_id}`}>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#FAFAF6] transition-colors" onClick={onToggle} data-testid={`pd-agency-toggle-${a.group_id}`}>
        {open ? <ChevronDown className="w-4 h-4 text-[#385C4B] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#385C4B] shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-[15px] text-[#1A1A1A] truncate">{a.name}</h3>
            <span className="text-[11px] font-mono font-semibold px-2 py-0.5 rounded-full bg-[#385C4B]/10 text-[#385C4B]">{a.rows.length} {tab}</span>
            {tab === "pending" && a.urgent > 0 && <span className="text-[11px] font-mono font-semibold px-2 py-0.5 rounded-full bg-[#D9534F]/10 text-[#C23B37]">{a.urgent} urgent</span>}
            <span className="text-[10px] font-mono uppercase text-[#8A8A80]">{a.company}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-[#6B6B61]">
            {a.company === "INDANE" && (
              <span className="inline-flex flex-wrap items-center gap-1" data-testid={`pd-agency-dm-${a.group_id}`}>
                <HardHat className="w-3.5 h-3.5 text-[#8A6046]" />
                {dms.length ? dms.map((d) => <span key={d.id} className="px-1.5 py-0.5 rounded bg-[#8A6046]/10 text-[#8A6046] font-semibold">{d.display_name}</span>) : <span>No delivery man</span>}
              </span>
            )}
            {tab === "pending" && a.oldest && <span>· oldest {fmtIst(a.oldest)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
          {a.company === "INDANE" && <AgencyLoginButton a={a} isAdmin />}
          <button onClick={() => copyText(a.rows.map((r) => r.lpg_id).join("\n"), `${a.rows.length} IDs`)} className={smallBtn} data-testid={`pd-copy-ids-${a.group_id}`}>
            <Copy className="w-3 h-3" />IDs
          </button>
          <button onClick={() => navigate(`/chat?group=${a.group_id}`)} className={smallBtn} data-testid={`pd-open-chat-${a.group_id}`}>
            <MessageSquare className="w-3 h-3" />Chat
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-[#EDEDE5]">
          <div className={`hidden md:grid ${GRID} gap-x-4 px-4 py-2 bg-[#FAFAF6] ${lbl}`}>
            <span>LPG ID</span><span>Mobile</span><span>DAC</span><span>{tab === "pending" ? "Dispatched" : "DAC ✗ by"}</span><span className="text-right">{tab === "pending" ? "Actions" : "Resolution"}</span>
          </div>
          <div className="divide-y divide-[#F0F0EA]">
            {a.rows.map((r) => <AdminRow key={`${r.message_id}:${r.entry_index}`} r={r} tab={tab} hide={hide} busy={busy === `${r.message_id}:${r.entry_index}`} onMark={onMark} onResolve={onResolve} />)}
          </div>
        </div>
      )}
    </section>
  );
}

const ACTIONS = [
  ["correct", "Done", "bg-[#385C4B] text-white hover:bg-[#2D4A3C]", "pd-done"],
  ["wrong", "DAC ✗", "bg-[#E89B35]/15 text-[#A8620F] border border-[#E89B35]/60 hover:bg-[#E89B35]/25", "pd-dac-wrong"],
  ["cancelled", "Cancelled", "bg-[#8A6046]/10 text-[#8A6046] border border-[#8A6046]/40 hover:bg-[#8A6046]/20", "pd-cancel"],
];
const cp = "inline-flex items-center gap-1 font-mono text-[13px] text-[#1A1A1A] hover:text-[#385C4B] transition-colors";

function AdminRow({ r, tab, hide, busy, onMark, onResolve }) {
  return (
    <div className={`grid grid-cols-1 ${GRID} md:items-center gap-x-4 gap-y-1.5 px-4 py-3 transition-colors ${r.urgent ? "bg-[#FFF4F1]" : "hover:bg-[#FAFAF6]"}`} data-testid={`pd-row-${r.lpg_id}`}>
      <div className="flex items-center gap-2 min-w-0">
        <button onClick={() => copyText(r.lpg_id, "LPG ID")} className="font-mono font-bold text-sm flex items-center gap-1 min-w-0" data-testid={`pd-copy-lpg-${r.lpg_id}`}>
          <span className="truncate" data-testid="pd-lpg-text">{mask(r.lpg_id, hide)}</span><Copy className="w-3 h-3 text-[#8A8A80] shrink-0" />
        </button>
        {r.urgent && <span className="text-[9px] font-bold uppercase tracking-wider text-white bg-[#D9534F] px-1.5 py-0.5 rounded-full">Urgent</span>}
      </div>
      <div>
        {r.mobile ? (
          <button onClick={() => copyText(r.mobile, "mobile")} className={cp} data-testid={`pd-copy-mobile-${r.lpg_id}`}>{r.mobile}<Copy className="w-3 h-3 text-[#8A8A80]" /></button>
        ) : <span className="text-[#C0C0B8]">—</span>}
      </div>
      <div>
        {r.dac_code ? (
          <button onClick={() => copyText(r.dac_code, "DAC")} className={cp} data-testid={`pd-copy-dac-${r.lpg_id}`}><span className="md:hidden text-xs text-[#6B6B61]">DAC</span>{r.dac_code}<Copy className="w-3 h-3 text-[#8A8A80]" /></button>
        ) : <span className="text-[#C0C0B8]">—</span>}
      </div>
      <div className="text-xs text-[#6B6B61]">{tab === "pending" ? fmtIst(r.created_at) : `${r.marked_by} · ${fmtIst(r.marked_at)}`}</div>
      <div className="flex md:justify-end gap-1.5">
        {tab === "pending" && ACTIONS.map(([st, label, cls, tid]) => (
          <button key={st} disabled={busy} onClick={() => onMark(r, st)} data-testid={`${tid}-${r.lpg_id}`}
            className={`h-9 px-3.5 flex-1 md:flex-none rounded-lg text-xs font-bold transition-colors disabled:opacity-50 ${cls}`}>
            {busy && st === "correct" ? "…" : label}
          </button>
        ))}
        {tab === "complaint" && (
          <button onClick={() => onResolve(r)} data-testid={`pd-resolve-${r.lpg_id}`}
            className="h-9 px-4 flex-1 md:flex-none rounded-lg text-xs font-bold bg-[#385C4B] text-white hover:bg-[#2D4A3C] inline-flex items-center justify-center gap-1.5 transition-colors">
            <CheckCircle2 className="w-3.5 h-3.5" />Resolve
          </button>
        )}
      </div>
    </div>
  );
}
